import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  IconButton,
  InputAdornment,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Store as StoreIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  History as HistoryIcon,
  Clear as ClearIcon,
  Map as MapIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useStores, Store } from '../hooks/useApi';

interface StoreSelectImprovedProps {
  value: string;
  onChange: (storeId: string, store?: Store) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  allowManualInput?: boolean;
  showMap?: boolean;
  maxHeight?: number;
}

const StoreSelectImproved: React.FC<StoreSelectImprovedProps> = ({
  value,
  onChange,
  label = '店舗選択',
  placeholder = '店舗を検索して選択してください',
  required = false,
  error = false,
  helperText,
  disabled = false,
  allowManualInput = false,
  showMap = false,
  maxHeight = 400,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem('arcana-favorite-stores');
    return saved ? JSON.parse(saved) : [];
  });
  const [recentStores, setRecentStores] = useState<string[]>(() => {
    const saved = localStorage.getItem('arcana-recent-stores');
    return saved ? JSON.parse(saved) : [];
  });
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [manualInputMode, setManualInputMode] = useState(false);
  const [manualStoreId, setManualStoreId] = useState('');

  const { data: stores, isLoading, error: storesError } = useStores();

  // 選択された店舗の情報
  const selectedStore = stores?.find(store => store.id === value);

  // フィルタリング済みの店舗リスト
  const filteredStores = useMemo(() => {
    if (!stores) return [];
    
    let filtered = stores;
    
    // 検索クエリでフィルタリング
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(store => 
        store.name.toLowerCase().includes(query) ||
        (store.address && store.address.toLowerCase().includes(query))
      );
    }

    // お気に入り、最近使用、その他の順でソート
    return filtered.sort((a, b) => {
      const aIsFavorite = favorites.includes(a.id);
      const bIsFavorite = favorites.includes(b.id);
      const aIsRecent = recentStores.includes(a.id);
      const bIsRecent = recentStores.includes(b.id);

      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      if (aIsRecent && !bIsRecent) return -1;
      if (!aIsRecent && bIsRecent) return 1;
      
      return a.name.localeCompare(b.name);
    });
  }, [stores, searchQuery, favorites, recentStores]);

  // お気に入りの切り替え
  const toggleFavorite = (storeId: string) => {
    const newFavorites = favorites.includes(storeId)
      ? favorites.filter(id => id !== storeId)
      : [...favorites, storeId];
    
    setFavorites(newFavorites);
    localStorage.setItem('arcana-favorite-stores', JSON.stringify(newFavorites));
  };

  // 店舗選択時の処理
  const handleStoreSelect = (store: Store) => {
    onChange(store.id, store);
    setIsExpanded(false);
    setSearchQuery('');

    // 最近使用した店舗に追加
    const newRecent = [store.id, ...recentStores.filter(id => id !== store.id)].slice(0, 5);
    setRecentStores(newRecent);
    localStorage.setItem('arcana-recent-stores', JSON.stringify(newRecent));
  };

  // 手動入力の確定
  const handleManualInputSubmit = () => {
    if (manualStoreId.trim()) {
      onChange(manualStoreId.trim());
      setManualInputMode(false);
      setManualStoreId('');
      setIsExpanded(false);
    }
  };

  // 選択クリア
  const handleClear = () => {
    onChange('');
    setSearchQuery('');
    setIsExpanded(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
        <CircularProgress size={20} />
        <Typography>店舗情報を読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* メイン入力フィールド */}
      <TextField
        fullWidth
        label={label}
        placeholder={placeholder}
        value={selectedStore ? selectedStore.name : (manualInputMode ? manualStoreId : searchQuery)}
        onChange={(e) => {
          if (manualInputMode) {
            setManualStoreId(e.target.value);
          } else {
            setSearchQuery(e.target.value);
            setIsExpanded(true);
          }
        }}
        onFocus={() => !manualInputMode && setIsExpanded(true)}
        required={required}
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <StoreIcon color={error ? 'error' : 'action'} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {selectedStore && (
                  <Tooltip title="選択をクリア">
                    <IconButton size="small" onClick={handleClear}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {showMap && (
                  <Tooltip title="地図で確認">
                    <IconButton size="small" onClick={() => setShowMapDialog(true)}>
                      <MapIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {allowManualInput && (
                  <Tooltip title={manualInputMode ? "店舗一覧から選択" : "店舗IDを直接入力"}>
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setManualInputMode(!manualInputMode);
                        setSearchQuery('');
                        setManualStoreId('');
                      }}
                      color={manualInputMode ? 'primary' : 'default'}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </InputAdornment>
          ),
        }}
      />

      {/* 手動入力モードの確定ボタン */}
      {manualInputMode && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleManualInputSubmit}
            disabled={!manualStoreId.trim()}
            startIcon={<CheckIcon />}
          >
            確定
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManualInputMode(false);
              setManualStoreId('');
            }}
            startIcon={<CloseIcon />}
          >
            キャンセル
          </Button>
        </Box>
      )}

      {/* 店舗選択パネル */}
      <Collapse in={isExpanded && !manualInputMode} timeout="auto">
        <Card sx={{ mt: 1, maxHeight, overflow: 'auto' }}>
          <CardContent sx={{ p: 0 }}>
            {storesError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                店舗情報の取得に失敗しました
              </Alert>
            ) : filteredStores.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searchQuery ? '検索条件に一致する店舗が見つかりません' : '店舗が登録されていません'}
                </Typography>
              </Box>
            ) : (
              <Box>
                {filteredStores.map((store) => {
                  const isFavorite = favorites.includes(store.id);
                  const isRecent = recentStores.includes(store.id);
                  const isSelected = store.id === value;

                  return (
                    <Box key={store.id}>
                      <Card 
                        variant={isSelected ? "outlined" : "elevation"}
                        elevation={isSelected ? 0 : 1}
                        sx={{ 
                          m: 1,
                          cursor: 'pointer',
                          border: isSelected ? 2 : 0,
                          borderColor: isSelected ? 'primary.main' : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                        onClick={() => handleStoreSelect(store)}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              <StoreIcon />
                            </Avatar>
                            
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                  {store.name}
                                </Typography>
                                {isFavorite && (
                                  <Chip
                                    icon={<FavoriteIcon />}
                                    label="お気に入り"
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                  />
                                )}
                                {isRecent && !isFavorite && (
                                  <Chip
                                    icon={<HistoryIcon />}
                                    label="最近使用"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              
                              {store.address && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationIcon fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    {store.address}
                                  </Typography>
                                </Box>
                              )}
                              
                              <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                                ID: {store.id}
                              </Typography>
                            </Box>

                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(store.id);
                              }}
                              color={isFavorite ? 'secondary' : 'default'}
                            >
                              {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>

      {/* 地図ダイアログ（今後の機能拡張用） */}
      <Dialog open={showMapDialog} onClose={() => setShowMapDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>店舗位置の確認</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">
              地図機能は今後実装予定です
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMapDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreSelectImproved;